import { BYOStepId, LinkVariant } from 'api/contentful/contentTypes.cloverdotcom';
import cx from 'classnames';
import { Icon, IconType } from 'components/Icon/Icon';
import { BYOStepOrder } from 'features/build-your-own/BuildYourOwnModule';
import { useBuildYourOwn } from 'features/build-your-own/hooks/useBuildYourOwn';
import { useEventListener } from 'hooks/useEventListener';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './BottomNavBYO.module.scss';
import { SystemPreviewDrawer } from './components/SystemPreviewDrawer';
import { TextV2 } from 'components/TextV2/TextV2';
import { useSubscriptionsState } from 'hooks/useSubscriptionsState';
import { getProductsTotalSubscriptionPrice } from 'utils/DCSUtils';
import { useDCSProductsQuery } from 'hooks/DCS/useDCSProductsQuery';
import { useSelectedItems } from 'features/build-your-own/hooks/sub-hooks/useSelectedItems';
import { SproutButton } from 'components/Button/SproutButton';
import { Banner } from '@clover-web-lib-sprout/banner';

interface IBottomNavBYOProps {
  showDrawer: boolean;
  setShowDrawer: (show: boolean) => void;
}

export const BottomNavBYO: React.FC<IBottomNavBYOProps> = ({ showDrawer, setShowDrawer }) => {
  const { t } = useTranslation();
  const { subscriptionsState } = useSubscriptionsState();
  const { data: dcsProducts } = useDCSProductsQuery();
  const {
    currentStepData,
    currentStepId,
    isStepCompleted,
    softwarePlanCost,
    totalHardwareCost,
    handleNextClick,
    selectionState,
  } = useBuildYourOwn();
  const selectedItems = useSelectedItems(selectionState);
  const drawerRef = useRef<HTMLDivElement>(null);

  const nextSecondaryText = currentStepData?.nextButtonSecondaryText;
  const stepNum = BYOStepOrder.indexOf(currentStepData?.queryValue as BYOStepId) + 1;

  const { productsWithoutPlans: productsWithoutPlansCost } = totalHardwareCost;
  const subscriptionMonthlyPriceWithQuantity = selectedItems.reduce((acc, { quantity, dcsIds }) => {
    const subscriptionMonthlyPrice = getProductsTotalSubscriptionPrice(
      dcsIds,
      subscriptionsState,
      dcsProducts,
    );

    acc += subscriptionMonthlyPrice * quantity;
    return acc;
  }, 0);

  /**
   * progress percentage is calculated by dividing the current step
   * number by one more than the total number of steps
   * i.e. step 1 out of 4, should equal 20% progress
   */
  const progressBarPercent = (stepNum / (BYOStepOrder.length + 1)) * 100;

  const handleClickOutside = (event: MouseEvent) => {
    if (!drawerRef.current || drawerRef.current.contains(event.target as Node)) {
      return;
    }

    setShowDrawer(false);
  };

  useEventListener('click', handleClickOutside);

  if (!currentStepData) return null;

  const handleDrawerToggle = (event: React.MouseEvent) => {
    setShowDrawer(!showDrawer);
    event.stopPropagation();
  };

  const handleNextBtnClick = (event: React.MouseEvent) => {
    handleNextClick(event, selectionState);
    setShowDrawer(false);
  };

  return (
    <footer className={styles.footer}>
      {!showDrawer && (
        <Banner
          className={cx(styles.infoBannerBox, styles.infoBoxShow, styles.container, styles.animateBottom)}
          ariaAtomic="true"
          ariaLive="assertive"
          variant="info"
          closeable={false}
        >
          <span>
            {t('byoBottomNav-notification.pre-link-text')}
            <a href={t('byoBottomNav-notification.help-link.link')} target="_blank" rel="noopener noreferrer">
              {t('byoBottomNav-notification.help-link.link-text')}
            </a>
            {t('byoBottomNav-notification.post-link-text')}
          </span>
        </Banner>
      )}

      <div className={styles.progressBar}>
        <div
          className={styles.progressFilling}
          style={{ transform: `translateX(${progressBarPercent}%)` }}
          aria-label={`you are on step ${stepNum} out of ${BYOStepOrder.length}`}
        />
      </div>
      <div className={styles.content}>
        <button
          onClick={handleDrawerToggle}
          aria-label={`${showDrawer ? t('close') : t('open')} ${t('build-your-own.nav.toggleDrawerLabel')}`}
          className={styles.drawerInfo}
        >
          <Icon
            type={showDrawer ? IconType.CHEVRON_DOWN : IconType.CHEVRON_UP}
            className={cx(styles.toggleDrawerIcon, { [styles.drawerOpen]: showDrawer })}
            aria-hidden
          />
          <div className={styles.systemPricing}>
            <div>
              <TextV2
                className={styles.primaryPrice}
                value={`${t('build-your-own.pricing.monthly', {
                  price: subscriptionMonthlyPriceWithQuantity + softwarePlanCost,
                })}`}
              />
            </div>
            {productsWithoutPlansCost > 0 && (
              <div>
                <TextV2
                  value={`+ ${t('build-your-own.pricing.upfront', {
                    price: productsWithoutPlansCost,
                  })}`}
                />
                <TextV2 value={t('build-your-own.nav.upfrontText')} />
              </div>
            )}
          </div>
        </button>
        {currentStepId && (
          <SproutButton
            onClick={handleNextBtnClick}
            variant={LinkVariant.PRIMARY}
            disabled={!isStepCompleted(currentStepId)}
            className={styles.nextBtn}
          >
            <div className={styles.nextContainer}>
              <div className={styles.nextText}>
                {nextSecondaryText && <span>{nextSecondaryText}</span>}
                <span className={styles.primaryText}>{currentStepData.nextButtonPrimaryText}</span>
              </div>
              <Icon className={styles.nextIcon} type={IconType.ARROW_RIGHT} aria-hidden />
            </div>
          </SproutButton>
        )}
      </div>
      <SystemPreviewDrawer showDrawer={showDrawer} drawerRef={drawerRef} />
    </footer>
  );
};
